import React, { useCallback, useContext, useEffect, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Checkbox,
  ListItemText,
  FormHelperText,
  Box,
  Container,
  Typography,
  LinearProgress,
  Stack
} from "@mui/material";
import { CountryCode } from "libphonenumber-js";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  CreateEmployeeFormData,
  CreateEmployeeRequest,
  CreateEmployeeResponse,
  EmploymentDuration,
  EmploymentPosition,
  EmploymentPositionLevel,
  EmploymentType,
  PermissionRole
} from "../../Graphql/types/employee";
import { isValidPhoneNumberForCountry } from "../../Utils/phoneValidatorUtil";
import { RwandaLocationParser } from "../../LocationsParser/RwandaLocationParser";
import { getEighteenYearsAgo } from "./utils/dateUtils";
import { getCreateEmployeeRequest } from "./utils/formUtils";
import { createEmployeeQuery } from "../../Graphql/querries/employeeQuery";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import { EMPLOYEE_PORTAL } from "../Routes";
import WithAuth from "../../Hoc/WithAuth";

const countryCode = process.env.REACT_APP_COUNTRY_CODE || "RW";
const urlRegex =
  /https?:\/\/(www\.)?[a-zA-Z0-9-._]+(\.[a-zA-Z]{2,})+(\/\S*)?\.(png|jpg|jpeg)$/i;

const CreateEmployee: React.FC = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const countries = ["RWANDA"];
  const [locationData, setLocationData] = useState({});
  const [provinces, setProvinces] = useState<string[]>([]);
  const [districts, setDistricts] = useState<string[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  const [cells, setCells] = useState<string[]>([]);
  const [villages, setVillages] = useState<string[]>([]);
  const navigate = useNavigate();

  // Initialize the instance when component loads and keep it unchanged
  const getRwandaLocationParser = useCallback(
    () => new RwandaLocationParser(),
    []
  );

  // initialize location data
  useEffect(() => {
    getRwandaLocationParser()
      .getLocationData()
      .then((data) => setLocationData(data));
  }, []);

  // Validation Schema
  const schema: yup.ObjectSchema<CreateEmployeeFormData> = yup.object().shape({
    firstName: yup
      .string()
      .required("Firstname is required")
      .min(2, "Firstname must be at least 2 chars")
      .matches(/^[A-Za-z\s'-]+$/, "Special chars are not allowed"),
    lastName: yup
      .string()
      .required("Lastname is required")
      .min(2, "Lastname must be at least 3 chars")
      .matches(/^[A-Za-z\s'-]+$/, "Special chars are not allowed"),
    avatar: yup
      .string()
      .required("Avatar is required")
      .matches(urlRegex, "Avatar must be a valid URL"),
    phoneNumber: yup
      .string()
      .required("PhoneNumber is required")
      .test("is-valid-phone", "Please enter a valid phone number", (value) =>
        value
          ? isValidPhoneNumberForCountry(value, countryCode as CountryCode)
          : false
      ),
    dateOfBirth: yup
      .date()
      .typeError("Invalid date format")
      .max(getEighteenYearsAgo(), "Employee must be at least 18 years old")
      .required("Date of birth is required"),
    positionLevel: yup
      .string()
      .oneOf(
        Object.values(EmploymentPositionLevel),
        "Invalid Position lvel selected"
      )
      .required("Position level is required")
      .min(2, "Position level must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    employmentType: yup
      .string()
      .oneOf(Object.values(EmploymentType), "Invalid Employment Type selected")
      .required("Employment Type is required")
      .min(2, "Employment Type must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    role: yup
      .string()
      .oneOf(Object.values(PermissionRole), "Invalid Job role selected")
      .required("Job role is required")
      .min(2, "Job role must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    employmentDuration: yup
      .string()
      .oneOf(Object.values(EmploymentDuration), "Invalid Duration selected")
      .required("Duration is required")
      .min(2, "Duration must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    positions: yup
      .array()
      .of(
        yup
          .string()
          .oneOf(Object.values(EmploymentPosition), "Invalid position selected")
          .required()
      )
      .min(1, "Select at least one position")
      .required("Positions are required"),

    country: yup
      .string()
      .oneOf(Object.values(countries), "Invalid country selected")
      .required("Country is required")
      .min(2, "Country must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    province: yup
      .string()
      .oneOf(Object.values(provinces), "Invalid province selected")
      .required("Province is required")
      .min(2, "Province must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    district: yup
      .string()
      .oneOf(Object.values(districts), "Invalid district selected")
      .required("District is required")
      .min(2, "District must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    sector: yup
      .string()
      .oneOf(Object.values(sectors), "Invalid sector selected")
      .required("Sector is required")
      .min(2, "Sector must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    cell: yup
      .string()
      .oneOf(Object.values(cells), "Invalid cell selected")
      .required("Cell is required")
      .min(2, "Cell must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    village: yup
      .string()
      .oneOf(Object.values(villages), "Invalid village selected")
      .required("Village is required")
      .min(2, "Village must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    hireDate: yup
      .date()
      .typeError("Invalid date format")
      .max(new Date(), "Date must be in the past")
      .required("Hire date is required")
  });

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<CreateEmployeeFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      lastName: "",
      avatar: "",
      phoneNumber: "",
      dateOfBirth: "" as unknown as Date,
      positionLevel: EmploymentPositionLevel.INTERN,
      employmentType: EmploymentType.EMPLOYEE,
      employmentDuration: EmploymentDuration.PART_TIME,
      role: PermissionRole.EMPLOYEE,
      positions: [],
      country: "",
      province: "",
      district: "",
      sector: "",
      cell: "",
      village: "",
      hireDate: "" as unknown as Date
    }
  });

  const selectedProvince = useWatch({ control, name: "province" });
  const selectedDistrict = useWatch({ control, name: "district" });
  const selectedSector = useWatch({ control, name: "sector" });
  const selectedCell = useWatch({ control, name: "cell" });

  // get provinces
  useEffect(() => {
    (async () => {
      setProvinces(getRwandaLocationParser().getProvinces(locationData));
    })();
  }, [locationData]);

  // Get districts based on provinces
  useEffect(() => {
    if (selectedProvince) {
      setDistricts(
        getRwandaLocationParser().getDistrictByProvince(
          selectedProvince,
          locationData
        )
      );
    }
  }, [selectedProvince]);

  // Get sectors based on districts
  useEffect(() => {
    if (selectedDistrict) {
      setSectors(
        getRwandaLocationParser().getSectors(
          selectedProvince,
          selectedDistrict,
          locationData
        )
      );
    }
  }, [selectedDistrict]);

  // Get cells based on sectors
  useEffect(() => {
    if (selectedSector) {
      setCells(
        getRwandaLocationParser().getCells(
          selectedProvince,
          selectedDistrict,
          selectedSector,
          locationData
        )
      );
    }
  }, [selectedSector]);

  // Get villages based on villages
  useEffect(() => {
    if (selectedCell) {
      setVillages(
        getRwandaLocationParser().getVillages(
          selectedProvince,
          selectedDistrict,
          selectedSector,
          selectedCell,
          locationData
        )
      );
    }
  }, [selectedCell]);

  const { mutate, isPending } = useMutation<
    CreateEmployeeResponse,
    Error,
    CreateEmployeeRequest
  >({
    mutationKey: ["(addEmployee"],
    mutationFn: (input) =>
      fetchGraphQL(createEmployeeQuery, { input }, setAlert),
    onSuccess: (response) => {
      // navigate to employee detail upon creation
      navigate(`${EMPLOYEE_PORTAL}/${response.addEmployee.id}`);
    }
  });

  const handleClickSubmit = (data: CreateEmployeeFormData) => {
    mutate(getCreateEmployeeRequest(data));
  };

  return (
    <Stack
      style={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        width: "100%",
        padding: "1em"
      }}
    >
      <Typography variant="h5" textAlign="center">
        Create a new Employee
      </Typography>
      <Stack
        style={{
          display: "flex",
          justifyContent: "start",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          variant="text"
          startIcon={<ArrowBackIcon />}
        >
          back
        </Button>
      </Stack>
      <Container
        maxWidth="md"
        className="jobFormContainer"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          flexDirection: "column",
          boxSizing: "border-box",
          padding: "0 2em",
          gap: 2
        }}
      >
        <Box
          component="form"
          noValidate
          autoComplete="on"
          style={{ width: "100%" }}
          display="flex"
          flexDirection="column"
          gap={1.7}
          onSubmit={handleSubmit(handleClickSubmit)}
        >
          {/* Text Fields */}
          {["firstName", "lastName", "avatar", "phoneNumber"].map(
            (fieldItem) => (
              <Controller
                key={fieldItem}
                name={fieldItem as unknown as keyof CreateEmployeeFormData}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors[fieldItem]}
                    size="medium"
                    id="Job Title"
                    label={`Enter ${fieldItem}`}
                    variant="outlined"
                    helperText={errors?.[fieldItem]?.message}
                    type="text"
                    fullWidth
                    margin="normal"
                    style={{ width: "100%!important" }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            )
          )}

          {/* Date Fields */}
          {["dateOfBirth", "hireDate"].map((fieldItem) => (
            <Controller
              key={fieldItem}
              name={fieldItem as keyof CreateEmployeeFormData}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`Select ${fieldItem}`}
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors[fieldItem]}
                  helperText={errors?.[fieldItem]?.message}
                  fullWidth
                  margin="normal"
                  style={{ width: "100%!important" }}
                  size="medium"
                />
              )}
            />
          ))}

          {/* Multi-Select Positions */}
          <Controller
            name="positions"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth margin="normal">
                <InputLabel id="positions-label" shrink>
                  Positions
                </InputLabel>
                <Select
                  multiple
                  {...field}
                  renderValue={(selected) => (selected as string[]).join(", ")}
                  error={!!errors.positions}
                  size="medium"
                  labelId="positions-label"
                  name="positions"
                >
                  {Object.values(EmploymentPosition).map((position) => (
                    <MenuItem key={position} value={position}>
                      <Checkbox checked={field?.value?.includes(position)} />
                      <ListItemText primary={position} />
                    </MenuItem>
                  ))}
                </Select>
                {!!errors.positions && (
                  <FormHelperText sx={{ color: "rgb(211, 47, 47)", pl: "1em" }}>
                    {errors.positions.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />

          {/* Dropdowns */}
          {[
            {
              name: "positionLevel",
              values: Object.values(EmploymentPositionLevel)
            },
            { name: "employmentType", values: Object.values(EmploymentType) },
            {
              name: "employmentDuration",
              values: Object.values(EmploymentDuration)
            },
            { name: "role", values: Object.values(PermissionRole) },
            { name: "country", values: countries },
            { name: "province", values: provinces },
            { name: "district", values: districts },
            { name: "sector", values: sectors },
            { name: "cell", values: cells },
            { name: "village", values: villages }
          ].map(({ name, values }) => (
            <Controller
              key={name}
              name={name as keyof CreateEmployeeFormData}
              control={control}
              render={({ field }) => (
                <FormControl fullWidth margin="normal">
                  <InputLabel>{name}</InputLabel>
                  <Select
                    {...field}
                    value={field.value ?? ""}
                    error={!!errors[name as keyof CreateEmployeeFormData]}
                  >
                    {values.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!errors[name as keyof CreateEmployeeFormData] && (
                    <FormHelperText error>
                      {errors[name as keyof CreateEmployeeFormData]?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          ))}

          {isPending && (
            <Stack width="100%" paddingBottom="1em" alignSelf="center">
              <LinearProgress />
            </Stack>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            style={{
              marginTop: "1em",
              width: "40%",
              padding: "0.8em",
              margin: "auto"
            }}
          >
            Create Employee
          </Button>
        </Box>
      </Container>
    </Stack>
  );
};

export default WithAuth(CreateEmployee);
